import * as React from "react";

import type { SpecType } from "@/api/contracts/specifications";
import type { ItemDialogSeed } from "@/components/catalog/item-detail-dialog";
import { ChangedFromBase } from "@/components/ui/changed-from-base";
import { CustomBadge } from "@/components/ui/custom-badge";
import { ErrorSnackbar } from "@/components/ui/error-snackbar";
import { HeroImageBox } from "@/components/ui/hero-image-box";
import { MuiLink } from "@/components/ui/mui-link";
import { MuiDehazeIcon, MuiLockIcon } from "@/components/ui/mui-spec-icons";
import { LEN_TOOLTIP_DELAY_MS, MuiTooltip } from "@/components/ui/mui-tooltip";
import { MuiTypography } from "@/components/ui/mui-typography";
import { OldBesideNew, OldValueAlone } from "@/components/ui/old-beside-new";
import { RemovedRoomNotes, RoomChangeList } from "@/components/ui/room-change-list";
import { StatusItemSelect } from "@/components/ui/status-item-select";
import type { SpecItemStatus } from "@/components/ui/spec-item-status";
import { TruncatedNotes } from "@/components/ui/truncated-notes";
import { useUpdateSpecLineStatus } from "@/hooks/spec-editor-lines";
import type { SpecEditorTarget } from "@/hooks/spec-editor-target";
import type { SpecLinesLine, SpecLinesSelection } from "@/hooks/specifications";
import {
  EDITOR_PAID_UPGRADE_TEXT,
  EDITOR_PER_PLAN_TEXT,
  NOT_PAID_UPGRADE_TEXT,
  NOT_PER_PLAN_TEXT,
  editorNotesPairs,
  editorNotesTableChanges,
  editorRowBasePairs,
  editorRowChangeSentence,
  type NotesTableChanges,
  type RowBasePairs,
} from "@/lib/spec-base-changes";
import { ERRORS } from "@/lib/spec-editor-errors";
import {
  ITEM_NAME_MAX,
  MODEL_NUMBER_MAX,
  editorLineName,
  editorRowState,
  editorRows,
  heroBadges,
  modelNumberText,
  selectionRowName,
  truncateText,
  type EditorRowKind,
  type EditorRowState,
  type EditorStateKind,
} from "@/lib/spec-lines-display";
import { cn } from "@/lib/utils";

import type { SpecActiveRow } from "./spec-line-dialogs";
import { GhostedPicture, ItemNameLink, NotesPair, Paired } from "./spec-line-rows";

// One line of the draft as the Editor draws it (#809) — the donor's
// `EditableLine` and `LineContentRow` (`SpecEditIsland/components/MainContent/
// Lines/`), drawn through the edit page's one renderer site (#806). The parity
// enumeration on #809 names the source line of every value below.
//
// **The block** is a white column whose rows follow `editorRows`: one row for a
// line with one shown selection (or none); for more, a header row, then per
// selection its indented row and its notes; then the line's notes. A deleted
// line shows only with "Show changes from Base" on, struck through.
//
// **A row** is the donor's six-cell grid — one line at `md` and up, two below
// it — whose look is `editorRowState`'s named facts: the Active selection's
// blues, the hover steps (the block's grey, the deeper blue, a hovered
// selection row's own step), the changed tints with the switch on, the strike,
// and the bottom rule. A click anywhere on a row sets the Active selection to
// that row's line and selection — a header row's is its first shown selection
// — except on the pill, the lock and the slots, which stop it. The item's name
// and picture open the item and let the click go on.
//
// **The status pill and the lock** write through #800's optimistic verb: the
// new colour at once, the row made active as the menu closes, and on a failure
// the old status back with `ERRORS.UPDATE_LINE_STATUS` on the line's own
// snackbar. The Active selection holds ids, so it needs no rollback of its own.
//
// **The old beside the new** (#1104; ledger §7, "Changed from base widens"):
// with "Show changes from Base" on, each changed value of a flagged row draws
// as #1083's pair — its base value struck, an arrow, then the value as drawn
// without the switch — per the #1043 brief's Editor column: the name as one
// piece, the old location type over the select and the room change list under
// it, the item swap's ghost picture, the badge, the item name and the `#` JDE,
// the model number, per-plan both ways and its manufacturer, and the notes as
// blocks — a removed note on a notes row the source never draws there
// (`editorRows`, #1060 Q7), and a specific line's room notes in its table
// (`notesChanges`, #1060 Q9). Which values pair is `editorRowBasePairs`'s
// rule, the icon's sentence `editorRowChangeSentence`'s. A struck or added row
// draws none. At every width, as the tints show at every width; with the
// switch off, nothing new draws (D1).
//
// **Slots the edit page fills** (#810): the locations select under the name,
// the specific-location notes table under the line, the ⋮ in the last cell,
// and the per-plan manufacturer link's opener. Without them the name stands
// alone, a specific line draws no notes table, the last cell holds the lock or
// nothing, and the per-plan links open nothing.

const CHILD_OF_ICON = "/child-of.svg";

/** What #810 mounts on the rows. */
export interface SpecEditorLineSlots {
  /** The locations select, under a line-level row's name; never on a deleted line. */
  locations?: (line: SpecLinesLine) => React.ReactNode;
  /**
   * A `specific` line's per-location notes table, as its notes row.
   * `selected`: the line is the Active selection's and shows one selection —
   * the donor's `LineNotesRow` `selected`. `className`: the row's ground — the
   * line's tint, else none, so the block's own shows through (the donor's
   * `.locations { backgroundColor: 'unset' }` under the row's classes,
   * `LineNotesRow.tsx:39`, `lineUtils.ts:125-131`). `notesChanges`: with
   * "Show changes from Base" on, the line's note and its rooms' notes that
   * differ from the base, to draw beside the new (#1104); absent otherwise.
   */
  locationNotes?: (
    line: SpecLinesLine,
    facts: { selected: boolean; className: string; notesChanges?: NotesTableChanges },
  ) => React.ReactNode;
  /** The 3-dot menu, in the last cell of a line that is not finalized. */
  menu?: (facts: {
    line: SpecLinesLine;
    selection: SpecLinesSelection | undefined;
    kind: EditorRowKind;
    setActive: React.Dispatch<React.SetStateAction<SpecActiveRow | null>>;
  }) => React.ReactNode;
  /**
   * The per-plan links' opener — the manufacturer dialog, on the row `kind`
   * the link sits in: a single row and a selection row under a header are
   * different rows, each with its own dialog.
   */
  onOpenManufacturer?: (
    line: SpecLinesLine,
    selection: SpecLinesSelection | undefined,
    kind: "LINE_AND_SELECTION" | "SELECTION_ONLY",
  ) => void;
}

export interface SpecEditorLineProps {
  line: SpecLinesLine;
  specType: SpecType;
  /** "Show changes from Base". */
  showChanges: boolean;
  /**
   * The Active selection when it is on this line, else `null` — so a click
   * re-renders the lines it moves between and no others.
   */
  active: SpecActiveRow | null;
  /** The page's setter — the line menu's Per Plan reads the one it holds (#861 D3 a). */
  onActiveChange: React.Dispatch<React.SetStateAction<SpecActiveRow | null>>;
  /**
   * Opens the item dialog on the row's item, with the row it was clicked on —
   * the click also sets that row active, and the dialog's Select lands there
   * (#811).
   */
  onOpenItem: (item: ItemDialogSeed, row: SpecActiveRow) => void;
  /** The status write's target. */
  target: SpecEditorTarget;
  slots?: SpecEditorLineSlots;
}

/** The block — memoized on its props, as the donor memoizes `EditableLine`. */
export const SpecEditorLine = React.memo(function SpecEditorLine(props: SpecEditorLineProps) {
  const { line, showChanges, active, slots } = props;
  const rows = editorRows(line, showChanges);
  if (rows.length === 0) return null;
  const multi = rows.some((row) => row.kind === "SELECTION_ONLY");

  return (
    <div
      data-testid={`line-${line.id}`}
      className="group/line text-lennar-ink hover:bg-lennar-action-hover flex flex-col bg-white"
    >
      {rows.map((row) => {
        switch (row.kind) {
          case "SELECTION_NOTES":
            return (
              <NotesRow
                key={`notes-${row.selection.id}`}
                {...props}
                selection={row.selection}
                notes={row.selection.notes}
                struck={row.selection.deleted}
              />
            );
          case "LINE_NOTES":
            return line.locationType === "specific" ? (
              <React.Fragment key="line-notes">
                {slots?.locationNotes?.(line, {
                  selected: !multi && active?.lineId === line.id,
                  className: cn("bg-transparent", rowTint(rowStateOf(props, "NOTES", undefined))),
                  ...notesTableFacts(props),
                })}
              </React.Fragment>
            ) : (
              <NotesRow
                key="line-notes"
                {...props}
                selection={undefined}
                notes={line.notes}
                struck={false}
              />
            );
          default:
            return (
              <ContentRow
                // The first row keeps one key whatever its kind and selection, as
                // the donor's unkeyed first child does — so its write and its
                // snackbar outlive a switch flip or a selection added.
                key={row.kind === "SELECTION_ONLY" ? `selection-${row.selection.id}` : "head"}
                {...props}
                kind={row.kind}
                selection={row.selection}
              />
            );
        }
      })}
    </div>
  );
});

/** The notes table's changes from the base, with the switch on and anything to pair. */
function notesTableFacts({ line, specType, showChanges }: SpecEditorLineProps) {
  const notesChanges = showChanges ? editorNotesTableChanges(line, specType) : undefined;
  return notesChanges ? { notesChanges } : {};
}

/** The background a row's facts give it, at rest, in a hovered block, and under the pointer. */
function rowTint(state: EditorRowState) {
  return cn(
    state.selectedSelection
      ? "bg-spec-row-selected-selection group-hover/line:bg-spec-row-selected-selection-hover"
      : state.selectedLine
        ? "bg-spec-row-selected-line group-hover/line:bg-spec-row-selected-line-hover"
        : state.changedTint === "base"
          ? "bg-spec-changed-from-base"
          : state.changedTint === "core"
            ? "bg-digital-ops-grey-background"
            : null,
    // Stacked on the block's hover so they outrank its steps, as the donor's
    // nested `.hover-*:hover` rules do.
    state.hoverSelected && "group-hover/line:hover:bg-spec-row-selected-selection-pointer",
    state.hoverNotSelected && "group-hover/line:hover:bg-spec-row-pointer",
    state.deleted && "text-spec-row-deleted decoration-spec-row-deleted line-through",
  );
}

function rowStateOf(
  props: SpecEditorLineProps,
  kind: EditorStateKind,
  selection: SpecLinesSelection | undefined,
) {
  return editorRowState({
    kind,
    line: props.line,
    selection,
    specType: props.specType,
    showChanges: props.showChanges,
    active: props.active,
  });
}

interface ContentRowProps extends SpecEditorLineProps {
  kind: EditorRowKind;
  selection: SpecLinesSelection | undefined;
}

/**
 * One `LineContentRow`: the grid, its six cells, and the row's click. Each row
 * owns its status write and its snackbar, as each donor row does
 * (`LineContentRow.tsx:62-69,156-161`): a selection row's lock never fades the
 * header row's pill.
 */
function ContentRow(props: ContentRowProps) {
  const { line, kind, selection, specType, active, onActiveChange, onOpenItem, target } = props;
  const update = useUpdateSpecLineStatus();
  const setStatus = (status: SpecItemStatus) => {
    // The pick lands with the menu's close in one render, so nothing of the
    // menu outlives it — the donor's `setTimeout(0)` worked round its own
    // re-render leaving the backdrop up (#809 Assumption 9).
    onActiveChange({ lineId: line.id, selectionId: selection?.id ?? null });
    update.mutate({ ...target(), lineId: line.id, status });
  };
  const state = rowStateOf(props, kind, selection);
  const selectionRow = kind === "SELECTION_ONLY";
  const lineRow = !selectionRow;
  const finalized = line.status === "finalized";
  const item = selection?.item ?? null;
  const openItem = () => {
    if (item) {
      onOpenItem(
        { id: item.id, name: item.name, heroImage: item.heroImage },
        { lineId: line.id, selectionId: selection?.id ?? null },
      );
    }
  };
  const stop = (event: React.SyntheticEvent) => event.stopPropagation();
  // The old beside the new (#1104): none with the switch off (D1).
  const pairs = props.showChanges ? editorRowBasePairs(line, selection, specType, kind) : {};

  return (
    <>
      <div
        data-testid={
          kind === "LINE_ONLY" ? `line-${line.id}` : `line-${line.id}-option-${selection?.id}`
        }
        data-row-kind={kind}
        onClick={() => onActiveChange({ lineId: line.id, selectionId: selection?.id ?? null })}
        className={cn(
          "relative grid w-full cursor-pointer items-center gap-5 py-4 pr-2.5 pl-5 text-[0.875rem] [overflow-wrap:break-word]",
          "border-mui-grey-100 border-b border-solid",
          "grid-cols-[72px_minmax(0,1fr)_minmax(65px,100px)] [grid-template-areas:'content-1_content-2_content-6'_'content-3_content-4_content-5']",
          "md:grid-cols-[46px_minmax(0,215px)_72px_minmax(175px,1fr)_100px_65px] md:[grid-template-areas:'content-1_content-2_content-3_content-4_content-5_content-6']",
          state.noBorderBottom && "border-b-0",
          rowTint(state),
        )}
      >
        {/* The drag mark: hidden until the block is hovered, and then on the
          line-level rows only (`lineUtils.ts:145-147`). */}
        <MuiDehazeIcon
          className={cn(
            "text-spec-drag-mark invisible absolute top-[48px]",
            lineRow && "group-hover/line:visible",
          )}
        />

        <div className="[grid-area:content-1]">
          {selectionRow ? (
            // Inline on the cell's text baseline, as the donor's bare `<img>` sits —
            // not the base layer's centred block.
            <img
              src={CHILD_OF_ICON}
              alt=""
              width={17}
              height={17}
              className="ml-4 inline align-baseline"
            />
          ) : line.deleted ? null : (
            <StatusItemSelect
              status={line.status}
              isLoading={update.isPending}
              onStatusChange={setStatus}
            />
          )}
        </div>

        <div className="flex max-w-[215px] flex-col gap-2 [grid-area:content-2]">
          <MuiTypography>
            <Paired old={pairs.name}>
              {selectionRow && selection
                ? selectionRowName(selection)
                : editorLineName(
                    kind as "LINE_ONLY" | "LINE_AND_SELECTION",
                    line.name,
                    selection?.name,
                  )}
            </Paired>
          </MuiTypography>
          {lineRow && !line.deleted && props.slots?.locations ? (
            <LocationsChange pairs={pairs}>
              <div className="contents" onClick={stop}>
                {props.slots.locations(line)}
              </div>
            </LocationsChange>
          ) : null}
        </div>

        <div className="[grid-area:content-3]">
          {kind === "LINE_ONLY" ? null : (
            <RowPicture
              line={line}
              kind={kind}
              selection={selection}
              ghost={pairs.ghost}
              onOpen={openItem}
            />
          )}
        </div>

        <div className="[grid-area:content-4]">
          {kind === "LINE_ONLY" ? null : line.perPlan ? (
            <PerPlanSummary
              line={line}
              selection={selection}
              kind={kind}
              struck={state.deleted}
              pairs={pairs}
              onOpen={props.slots?.onOpenManufacturer}
            />
          ) : (
            <ItemSummary
              selection={selection}
              struck={state.deleted}
              pairs={pairs}
              onOpen={openItem}
            />
          )}
        </div>

        <div className="[grid-area:content-5]">
          {kind === "LINE_ONLY" ? null : (
            <ModelNumber
              perPlan={line.perPlan}
              item={item}
              isActive={selection !== undefined && active?.selectionId === selection.id}
              old={pairs.model}
            />
          )}
        </div>

        <div className="flex flex-row items-center justify-end [grid-area:content-6]">
          {state.changedTint !== null ? (
            <ChangedFromBase
              specType={specType}
              sentence={editorRowChangeSentence(kind, line, selection, specType) ?? undefined}
              className={finalized ? "mr-2" : undefined}
            />
          ) : null}
          {finalized ? (
            <LineLock onUnlock={() => setStatus("in_progress")} />
          ) : props.slots?.menu ? (
            <div className="contents" onClick={stop}>
              {props.slots.menu({ line, selection, kind, setActive: onActiveChange })}
            </div>
          ) : null}
        </div>
      </div>
      {/* Beside the row, not in it: the snackbar's clicks are not the row's. */}
      <ErrorSnackbar
        open={update.isError}
        message={ERRORS.UPDATE_LINE_STATUS}
        onClose={() => update.reset()}
      />
    </>
  );
}

/**
 * Cell 3 — `LineSelectionImage`: the shared 72px box, omitted with no picture
 * to draw and no stored item. A per-plan line shows its illustration, 48px
 * wide, with no marks and never pending.
 */
function RowPicture({
  line,
  kind,
  selection,
  ghost,
  onOpen,
}: {
  line: SpecLinesLine;
  kind: EditorRowKind;
  selection: SpecLinesSelection | undefined;
  /** The base side's picture, struck and small above this one (#1104). */
  ghost: RowBasePairs["ghost"];
  onOpen: () => void;
}) {
  const item = selection?.item ?? null;
  if (!line.perPlan && !item?.heroImage && !selection?.itemId) {
    // No picture to draw: an item cleared still shows the base side's ghost,
    // over *None* (#1104).
    return ghost ? (
      <div data-testid="selection-undefined-image">
        <GhostedPicture ghost={ghost} selection={selection}>
          <span className="text-[0.875em] italic">None</span>
        </GhostedPicture>
      </div>
    ) : null;
  }
  const hero = (
    <HeroImageBox
      image={item?.heroImage}
      perPlan={line.perPlan}
      narrowPerPlan
      alt={item?.name ?? ""}
      onClick={onOpen}
      isPendingApproval={!line.perPlan && !item?.isApproved}
      isDeleted={kind === "LINE_AND_SELECTION" ? line.deleted : Boolean(selection?.deleted)}
      isRetired={Boolean(item?.retired)}
      {...heroBadges(line, item)}
    />
  );
  return (
    <div data-testid={`selection-${item?.id}-image`} className={cn(item && "cursor-pointer")}>
      {/* As wide as the 72px picture, so the column keeps its width. */}
      <GhostedPicture ghost={ghost} selection={selection}>
        {hero}
      </GhostedPicture>
    </div>
  );
}

/**
 * The locations select, or — where its row pairs them (#1104) — the old
 * type's text struck above it with the elbow into it, and a specific line's
 * room change list under it, inside the elbow's indent when the type changed.
 * The old text is the row's, so a click on it sets the Active selection.
 */
function LocationsChange({ pairs, children }: { pairs: RowBasePairs; children: React.ReactNode }) {
  const now = pairs.rooms ? (
    <div className="flex flex-col items-start gap-1">
      {children}
      <MuiTypography variant="body2">
        <RoomChangeList rooms={pairs.rooms.current} removed={pairs.rooms.removed} />
      </MuiTypography>
    </div>
  ) : (
    children
  );
  return (
    <Paired old={pairs.locations} layout="block">
      {now}
    </Paired>
  );
}

/**
 * The paid-upgrade badge, or — where its row pairs it — its newly set mark
 * beside `Not a paid upgrade`, or a cleared one's struck text alone (#1104).
 */
function PaidUpgradeBadge({
  selection,
  change,
}: {
  selection: SpecLinesSelection | undefined;
  change: RowBasePairs["paidUpgrade"];
}) {
  const badge = <CustomBadge text={EDITOR_PAID_UPGRADE_TEXT} />;
  if (change === "set") return <OldBesideNew old={NOT_PAID_UPGRADE_TEXT}>{badge}</OldBesideNew>;
  if (change === "cleared") return <OldValueAlone>{EDITOR_PAID_UPGRADE_TEXT}</OldValueAlone>;
  return selection?.isPaidUpgrade ? badge : null;
}

/**
 * Cell 4, the item's half — `LineSelectionSummary`: the paid-upgrade badge,
 * the name cut at 50 under a `top` tooltip with the whole of it, or `No Item
 * Selected`; then ` #{jde}`.
 */
function ItemSummary({
  selection,
  struck,
  pairs,
  onOpen,
}: {
  selection: SpecLinesSelection | undefined;
  struck: boolean;
  /** The row's old values: the badge, the item name (old cut at 50) and the JDE (#1104). */
  pairs: RowBasePairs;
  onOpen: () => void;
}) {
  const item = selection?.item ?? null;
  return (
    <div className="flex flex-col">
      <PaidUpgradeBadge selection={selection} change={pairs.paidUpgrade} />
      <Paired old={pairs.itemName} layout="block" cutAt={ITEM_NAME_MAX}>
        {item ? (
          <ItemNameLink onClick={onOpen} struck={struck} underline={false}>
            <MuiTooltip
              title={<span>{item.name}</span>}
              side="top"
              delayMs={LEN_TOOLTIP_DELAY_MS}
              asChild
            >
              <span>{truncateText(item.name, ITEM_NAME_MAX)}</span>
            </MuiTooltip>
          </ItemNameLink>
        ) : (
          <MuiTypography variant="body2" color="secondary" className="pl-2">
            No Item Selected
          </MuiTypography>
        )}
      </Paired>
      {pairs.jde !== undefined ? (
        <MuiTypography variant="body2" color="secondary" className="sm:mt-2">
          <Paired old={pairs.jde}>{item?.jdeItemNumber ? `#${item.jdeItemNumber}` : ""}</Paired>
        </MuiTypography>
      ) : item?.jdeItemNumber ? (
        <MuiTypography variant="body2" color="secondary" className="sm:mt-2">
          {` #${item.jdeItemNumber}`}
        </MuiTypography>
      ) : null}
    </div>
  );
}

/**
 * Cell 4, a per-plan line's half — `LinePerPlan`: the badge, `Refer to plan
 * for details` (no period), then the selection's manufacturer as an underlined
 * grey link, or `Add manufacturer` while the line is not finalized. Both open
 * the manufacturer dialog (#810's slot).
 */
function PerPlanSummary({
  line,
  selection,
  kind,
  struck,
  pairs,
  onOpen,
}: {
  line: SpecLinesLine;
  selection: SpecLinesSelection | undefined;
  kind: "LINE_AND_SELECTION" | "SELECTION_ONLY";
  /** The row's strike, which a button inside never takes from it. */
  struck: boolean;
  /**
   * The row's old values (#1104): the badge, a line turned per-plan, and the
   * selection's manufacturer — whose pair sits in the link's place after the
   * colon's gap, `None` beside a removed one with "Add manufacturer" still
   * below it.
   */
  pairs: RowBasePairs;
  onOpen: SpecEditorLineSlots["onOpenManufacturer"];
}) {
  const manufacturer = selection?.manufacturer ?? null;
  const open = () => onOpen?.(line, selection, kind);
  const makerPaired = pairs.perPlanManufacturer !== undefined;
  const addLink =
    line.status !== "finalized" ? (
      <ManufacturerLink onClick={open} struck={struck}>
        Add manufacturer
      </ManufacturerLink>
    ) : null;
  return (
    <div
      data-testid="per-plan-manufacturer"
      className="flex flex-col items-start justify-center gap-1"
    >
      <PaidUpgradeBadge selection={selection} change={pairs.paidUpgrade} />
      <MuiTypography variant="body2">
        <Paired old={pairs.perPlan === undefined ? undefined : NOT_PER_PLAN_TEXT}>
          {EDITOR_PER_PLAN_TEXT}
        </Paired>
      </MuiTypography>
      {makerPaired ? (
        <>
          <MuiTypography variant="body2">
            Manufacturer:
            <span className="pl-2">
              <Paired old={pairs.perPlanManufacturer}>
                {manufacturer ? (
                  <ManufacturerLink onClick={open} struck={struck}>
                    {manufacturer.name}
                  </ManufacturerLink>
                ) : null}
              </Paired>
            </span>
          </MuiTypography>
          {manufacturer ? null : addLink}
        </>
      ) : manufacturer ? (
        <MuiTypography variant="body2">
          Manufacturer:
          <ManufacturerLink onClick={open} struck={struck} className="pl-2">
            {manufacturer.name}
          </ManufacturerLink>
        </MuiTypography>
      ) : (
        addLink
      )}
    </div>
  );
}

/**
 * The per-plan link — react-ui's `Link variant='body2'` at `text.secondary`,
 * `textDecoration: 'underline'` in its own ink. The donor's is an `<a>` with no
 * `href`; a `<button>` here, dressed the same (the item name's #695 D3 shape).
 */
function ManufacturerLink({
  onClick,
  struck,
  className,
  children,
}: {
  onClick: () => void;
  struck: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <MuiLink asChild color="textSecondary" variant="body2">
      <button
        type="button"
        onClick={onClick}
        className={cn("cursor-pointer border-0 bg-transparent p-0 text-left", className)}
      >
        {/* The donor's link takes the row's strike in the row's colour under its
            own grey underline; a button stops it, so it is drawn inside. */}
        {struck ? (
          <span className="decoration-spec-row-deleted line-through">{children}</span>
        ) : (
          children
        )}
      </button>
    </MuiLink>
  );
}

/**
 * Cell 5 — `LineModelNumber`; where its row pairs it (#1104), the old model
 * number beside it, cut at 25 as the cell cuts, the whole of it in `title`
 * and the announcement.
 */
function ModelNumber({
  old,
  ...facts
}: Parameters<typeof modelNumberText>[0] & { old: RowBasePairs["model"] }) {
  const text = modelNumberText(facts);
  // No item now: an item cleared still strikes its old model number over *None*.
  if (!text) return old === undefined ? null : <Paired old={old} cutAt={MODEL_NUMBER_MAX} />;
  const now = text.placeholder ? (
    <MuiTypography variant="body2" color="secondary">
      {text.text}
    </MuiTypography>
  ) : (
    <span>{text.text}</span>
  );
  return (
    <Paired old={old} cutAt={MODEL_NUMBER_MAX}>
      {now}
    </Paired>
  );
}

/**
 * The finalized line's lock — `LineLock`: a 24px `Lock` in `iconColor.main`,
 * `mr 8`, under a `top` tooltip. Its click sets the line back to In Progress
 * through the pill's write, and stops there. A `<button>` dressed as the bare
 * icon, named by its tooltip — the donor's is an SVG a keyboard never reaches
 * (#809 Assumption 8).
 */
function LineLock({ onUnlock }: { onUnlock: () => void }) {
  const title = "This line is finalized. To edit, unlock or change the line status.";
  return (
    <MuiTooltip title={title} side="top" delayMs={LEN_TOOLTIP_DELAY_MS} asChild>
      <button
        type="button"
        data-testid="lock-icon"
        aria-label={title}
        onClick={(event) => {
          event.stopPropagation();
          onUnlock();
        }}
        className="text-spec-icon mr-2 flex cursor-pointer border-0 bg-transparent p-0"
      >
        <MuiLockIcon />
      </button>
    </MuiTooltip>
  );
}

/**
 * A notes row — `SelectionNotesRow` and `LineNotesRow`'s plain branch: the
 * notes clamped to two lines with More / Less, `pl 16` over the row rule, with
 * a notes row's facts (the Active line's tint, a deleted line's strike); a
 * deleted selection's notes struck through as well. No click, no pointer.
 */
function NotesRow(
  props: SpecEditorLineProps & {
    selection: SpecLinesSelection | undefined;
    notes: string | null;
    struck: boolean;
  },
) {
  const state = rowStateOf(props, "NOTES", props.selection);
  // The old notes struck above the new as a block (#1104), `None` on an empty
  // side — a removed note's row is drawn for that alone (`editorRows`).
  const { notes: old, removedRooms } = props.showChanges
    ? editorNotesPairs(props.line, props.selection, props.specType)
    : {};
  return (
    <div
      data-row-kind="NOTES"
      className={cn(
        "border-mui-grey-100 border-b border-solid pl-4 text-[0.875rem]",
        rowTint(state),
      )}
    >
      {old !== undefined ? (
        <NotesPair old={old} notes={props.notes} className="py-1">
          <TruncatedNotes notes={props.notes} />
        </NotesPair>
      ) : (
        <TruncatedNotes
          notes={props.notes}
          className={cn("py-1", props.struck && "line-through")}
        />
      )}
      {removedRooms ? <RemovedRoomNotes rooms={removedRooms} /> : null}
    </div>
  );
}
